interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  dependencies: string[];
  accessCount: number;
  lastAccessed: number;
}

interface CacheConfig {
  maxAge: number; // ms
  maxSize: number;
  dependencies?: string[];
}

class SmartCacheManager {
  private pageCache = new Map<string, CacheEntry>();
  private componentCache = new Map<string, CacheEntry>();
  private defaultConfig: CacheConfig = {
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxSize: 50
  };

  // Page-level cache
  setPageCache<T>(key: string, data: T, config?: Partial<CacheConfig>): void {
    const finalConfig = { ...this.defaultConfig, ...config };
    
    this.pageCache.set(key, {
      data,
      timestamp: Date.now(),
      dependencies: finalConfig.dependencies || [],
      accessCount: 0,
      lastAccessed: Date.now()
    });

    this.enforceSize(this.pageCache, finalConfig.maxSize);
  }

  getPageCache<T>(key: string): T | null {
    const entry = this.pageCache.get(key);
    if (!entry) return null;

    if (this.isExpired(entry)) {
      this.pageCache.delete(key);
      return null;
    }

    entry.accessCount++;
    entry.lastAccessed = Date.now();
    return entry.data as T;
  }

  // Component-level cache
  setComponentCache<T>(key: string, data: T, config?: Partial<CacheConfig>): void {
    const finalConfig = { ...this.defaultConfig, ...config };
    
    this.componentCache.set(key, {
      data,
      timestamp: Date.now(),
      dependencies: finalConfig.dependencies || [],
      accessCount: 0,
      lastAccessed: Date.now()
    });

    this.enforceSize(this.componentCache, finalConfig.maxSize);
  }

  getComponentCache<T>(key: string): T | null {
    const entry = this.componentCache.get(key);
    if (!entry) return null;

    if (this.isExpired(entry)) {
      this.componentCache.delete(key);
      return null;
    }

    entry.accessCount++;
    entry.lastAccessed = Date.now();
    return entry.data as T;
  }

  // Smart invalidation
  invalidateByDependency(dependency: string): void {
    // Page cache invalidation
    for (const [key, entry] of this.pageCache.entries()) {
      if (entry.dependencies.includes(dependency)) {
        this.pageCache.delete(key);
      }
    }

    // Component cache invalidation
    for (const [key, entry] of this.componentCache.entries()) {
      if (entry.dependencies.includes(dependency)) {
        this.componentCache.delete(key);
      }
    }
  }

  invalidateStaleData(): void {
    const now = Date.now();
    
    // Remove expired entries
    for (const [key, entry] of this.pageCache.entries()) {
      if (this.isExpired(entry)) {
        this.pageCache.delete(key);
      }
    }

    for (const [key, entry] of this.componentCache.entries()) {
      if (this.isExpired(entry)) {
        this.componentCache.delete(key);
      }
    }
  }

  // Progressive cache warming
  async warmCache(routes: string[]): Promise<void> {
    for (const route of routes) {
      if (!this.pageCache.has(route)) {
        // Simulate data loading for critical routes
        try {
          // This would be replaced with actual data fetching
          await new Promise(resolve => setTimeout(resolve, 10));
        } catch (error) {
          console.warn(`Cache warming failed for route: ${route}`);
        }
      }
    }
  }

  // Cache statistics
  getStats() {
    return {
      pageCache: {
        size: this.pageCache.size,
        hitRate: this.calculateHitRate(this.pageCache)
      },
      componentCache: {
        size: this.componentCache.size,
        hitRate: this.calculateHitRate(this.componentCache)
      }
    };
  }

  clearAll(): void {
    this.pageCache.clear();
    this.componentCache.clear();
  }

  clearPageCache(): void {
    this.pageCache.clear();
  }

  clearComponentCache(): void {
    this.componentCache.clear();
  }

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > this.defaultConfig.maxAge;
  }

  private enforceSize(cache: Map<string, CacheEntry>, maxSize: number): void {
    if (cache.size <= maxSize) return;

    // LRU eviction
    const entries = Array.from(cache.entries());
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

    const toRemove = entries.slice(0, cache.size - maxSize);
    toRemove.forEach(([key]) => cache.delete(key));
  }

  private calculateHitRate(cache: Map<string, CacheEntry>): number {
    if (cache.size === 0) return 0;
    
    const totalAccess = Array.from(cache.values())
      .reduce((sum, entry) => sum + entry.accessCount, 0);
    
    return totalAccess / cache.size;
  }
}

export const smartCacheManager = new SmartCacheManager();
export type { CacheConfig };
